import type { ReactNode } from 'react';
import { Icon } from '../common';
import { ScopeTabs } from './ScopeTabs';
import { ResultCluster } from './ResultCluster';
import {
  MIN_QUERY_LENGTH,
  recordKey,
  SCOPES,
  type SearchCluster,
  type SearchRecord,
  type SearchResult,
  type SearchScope,
} from './searchTypes';

// Centred empty state — reuses the shared `.pane-empty-*` pieces (04-drawers.css)
// so every empty surface in the app reads the same.
function Empty({ icon, title, hint }: { icon: string; title: ReactNode; hint: ReactNode }) {
  return (
    <div className="gs-empty">
      <div className="pane-empty-card">
        <div className="pane-empty-icon">
          <Icon name={icon} size={22} />
        </div>
        <div className="pane-empty-title">{title}</div>
        <div className="pane-empty-hint">{hint}</div>
      </div>
    </div>
  );
}

// The dropdown body: the scope tabs, then either Recent (no query) or Results
// (a scoped flat list, or household clusters), or the appropriate empty state.
// Presentational — GlobalSearch owns the query/keyboard state and passes the
// active row key down. Ported from the prototype's render().
export function SearchPanel({
  q,
  scope,
  result,
  loading,
  recent,
  canSeeUsers,
  activeKey,
  viewAll,
  onScope,
  onOpen,
}: {
  q: string;
  scope: SearchScope;
  result: SearchResult | null;
  loading: boolean;
  recent: SearchCluster[];
  canSeeUsers: boolean;
  activeKey: string | null;
  viewAll: (() => void) | null;
  onScope: (s: SearchScope) => void;
  onOpen: (rec: SearchRecord) => void;
}) {
  const term = q.trim();
  const scopeLabel = SCOPES.find((s) => s.key === scope)?.label ?? '';

  return (
    <div className="popover gs-panel" role="listbox" aria-label="Search results">
      <ScopeTabs scope={scope} onScope={onScope} canSeeUsers={canSeeUsers} />

      {term === '' ? (
        // ---- Recent ----
        <>
          <div className="gs-head">
            <span>Recent</span>
          </div>
          {recent.length > 0 ? (
            <div className="gs-scroll">
              {recent.map((c) => (
                <ResultCluster
                  key={recordKey(c.header)}
                  cluster={c}
                  q=""
                  activeKey={activeKey}
                  onOpen={onOpen}
                />
              ))}
            </div>
          ) : (
            <Empty icon="clock" title="No recent items" hint="Anything you open shows up here." />
          )}
        </>
      ) : term.length < MIN_QUERY_LENGTH ? (
        // ---- Below the minimum: nudge, don't search ----
        <Empty
          icon="search"
          title="Keep typing…"
          hint={`Type at least ${MIN_QUERY_LENGTH} characters to search.`}
        />
      ) : !result ? (
        // ---- Loading (result cleared while the new query is in flight) ----
        <>
          <div className="gs-head">
            <span>Results</span>
          </div>
          <div className="gs-loading">{loading ? 'Searching…' : ''}</div>
        </>
      ) : result.clusters.length === 0 ? (
        // ---- No results ----
        <>
          <div className="gs-head">
            <span>Results</span>
          </div>
          <Empty
            icon="search"
            title={
              <>
                No results for “{term}”
                {scope !== 'all' ? ` in ${scopeLabel.toLowerCase()}` : ''}
              </>
            }
            hint={`${scope !== 'all' ? 'Try “All”, or a' : 'Try a'} name, breed, phone, email, or role.`}
          />
        </>
      ) : (
        // ---- Results: scoped flat list, or household clusters ----
        <>
          <div className="gs-head">
            <span>{scope === 'all' ? 'Results' : scopeLabel}</span>
            <span className="gs-count">
              {result.matchCount} match{result.matchCount === 1 ? '' : 'es'}
              {result.capped ? '+' : ''}
            </span>
          </div>
          <div className="gs-scroll">
            {/* Every scope clusters with context now — a scoped dog still heads
                its own cluster with the owner nested; a helper nests their leave. */}
            {result.clusters.map((c) => (
              <ResultCluster
                key={recordKey(c.header)}
                cluster={c}
                q={q}
                activeKey={activeKey}
                onOpen={onOpen}
              />
            ))}
          </div>
        </>
      )}

      {/* Scoped-search footer — jump to the type's full listing page. Pinned to
          the panel bottom; the arrow nudges on hover. */}
      {viewAll && (
        <button type="button" className="gs-viewall" onClick={viewAll}>
          <span>View all</span>
          <Icon name="right" size={16} className="gs-viewall-arrow" />
        </button>
      )}
    </div>
  );
}
